import React, { useState, useEffect, useContext, useMemo } from "react";
import { Title, Text } from "rizzui/typography";
import { Button, Input, Password, Textarea } from "rizzui";
import AccountBox from "../../component/accountBox";
import { Controller, useForm } from "react-hook-form";
import SocialLogin from "../../component/socialsLogIn";
import { useNavigate } from "react-router";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { baseUrl } from "./login";
import { AuthContext } from "../../context/authContext";
import {
  inputClassName,
  labelClassName,
  textAreaClassName,
} from "../projects/new/first";
import CountryDropdown, {
  getCountryOptions,
} from "../../component/countrySelect";
import CustomPhoneInput from "../../component/phoneInput";

export const emailValidationPattern = {
  required: {
    value: true,
    message: "Email address is required",
  },
  pattern: {
    // eslint-disable-next-line
    value: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/g,
    message: "This is not a valid email address",
  },
};
export const passwordValidationPattern = {
  required: {
    value: true,
    message: "Password is required",
  },
  minLength: {
    value: 8,
    message: "Password length must be least 8 characters long",
  },
};

export default function SignUp() {
  useEffect(() => {
    document.title = "Create an account";
  }, []);
  const navigate = useNavigate();
  const { setCurrentEmail } = useContext(AuthContext);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
    control,
    watch,
    setValue,
  } = useForm({});

  const [selectedCountry, setSelectedCountry] = useState<string | undefined>(
    undefined
  );

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === "businessCountry") {
        setSelectedCountry(value.businessCountry?.code || null);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const handlePhoneChange = (newValue: string, countryCode: string) => {
    setValue("phone", newValue); // Update phone field
    setValue("countryCode", countryCode); // Update phoneCode field
    setValue("businessCountry", { code: countryCode }); // Update businessCountry field
    setSelectedCountry(countryCode); // Update selectedCountry state
  };

  const signup = async () => {
    const {
      firstName,
      lastName,
      businessName,
      businessCountry,
      phone,
      email,
      password,
      business_about,
      countryCode,
    } = getValues();
    try {
      const response = await axios.post(`${baseUrl}/auth/register`, {
        firstname: firstName,
        lastname: lastName,
        email,
        password,
        business_name: businessName,
        business_about,
        business_country: getCountryOptions().find(
          (country) => country.code === businessCountry.code
        )?.value,
        phone_number: "+" + phone,
        country_phone_code: countryCode,
      });
      return response;
    } catch (error) {
      const message =
        error?.response?.data?.details?.message ||
        "Something went wrong, try again";
      throw new Error(message);
    }
  };

  const mutation = useMutation({
    mutationFn: signup,
    onSuccess: (response) => {
      const verificationToken = response?.data?.verification_token;
      if (verificationToken) {
        setCurrentEmail!(getValues().email); // or pass email separately
        navigate("/account/login");
      }
    },
    onError: (error) => {
      const message = error?.message || "Something went wrong, try again";
    },
  });

  const handleSignUp = () => {
    mutation.mutate();
  };

  return (
    <AccountBox isLogin={true}>
      <div className="flex flex-col gap-[32px]">
        <div>
          <Title className="text-[18px] leading-[23.4px] font-[600] text-primary-dark mb-[8px] font-ibmPlexSerif">
            Create account
          </Title>
          <Text className="text-[12px] leading-[18.48px] font-[400] text-secondary-dark font-switzer">
            Create an account to automate your customer outreach
          </Text>
        </div>

        <div className="text-secondary-dark font-switzer">
          <SocialLogin />
          <form
            onSubmit={handleSubmit(handleSignUp)}
            noValidate
            className="flex flex-col gap-[24px]"
          >
            <div className="flex flex-row justify-between gap-5">
              <Input
                type="text"
                label="First Name"
                variant="text"
                rounded="md"
                inputClassName={inputClassName}
                labelClassName={labelClassName}
                {...register("firstName", {
                  required: {
                    value: true,
                    message: "First name is required",
                  },
                })}
                error={
                  errors.firstName?.message
                    ? "" + errors.firstName?.message
                    : ""
                }
              />

              <Input
                type="text"
                label="Last Name"
                variant="text"
                rounded="md"
                inputClassName={inputClassName}
                labelClassName={labelClassName}
                {...register("lastName", {
                  required: {
                    value: true,
                    message: "Last name is required",
                  },
                })}
                error={
                  errors.lastName?.message ? "" + errors.lastName.message : ""
                }
              />
            </div>
            <Input
              type="text"
              label="Business Name"
              variant="text"
              rounded="md"
              inputClassName={inputClassName}
              labelClassName={labelClassName}
              {...register("businessName", {
                required: {
                  value: true,
                  message: "Business name is required",
                },
              })}
              error={
                errors.businessName?.message
                  ? "" + errors.businessName.message
                  : ""
              }
            />

            <Textarea
              label="Tell us about your business"
              variant="outline"
              rounded="md"
              rows={3}
              textareaClassName={textAreaClassName}
              labelClassName={labelClassName}
              {...register("business_about", {
                required: {
                  value: true,
                  message: "Tell us about your business",
                },
              })}
              style={{ resize: "none" }}
              error={
                errors.business_about?.message
                  ? " " + errors.business_about.message
                  : ""
              }
            />

            <Controller
              control={control}
              name="businessCountry"
              rules={{
                required: {
                  value: true,
                  message: "Country of Business is Required",
                },
              }}
              render={({ field: { onChange, value, name } }) => (
                <div className="w-full">
                  <label className={labelClassName}>Country of Business</label>
                  <CountryDropdown
                    onChange={(code) => {
                      onChange({ code }); // Pass an object with the code
                    }}
                    value={value?.code} // Pass only the code
                    name={name}
                  />
                  {errors.businessCountry?.message && (
                    <p className="text-red text-[13px] mt-0.5 rizzui-textarea-error-text">
                      {"" + errors.businessCountry.message}
                    </p>
                  )}
                </div>
              )}
            />

            <Controller
              control={control}
              name="phone"
              rules={{
                required: {
                  value: true,
                  message: "Phone Number is Required",
                },
                pattern: {
                  value: /^.{5,}$/,
                  message: "Enter a valid phone number",
                },
              }}
              render={({ field: { onChange, value } }) => (
                <div className="w-full">
                  <label className={labelClassName}>Phone Number</label>
                  <CustomPhoneInput
                    onChange={({ phone, code }) => {
                      handlePhoneChange(phone, code); // Update phone and country
                    }}
                    value={value}
                    selectedCountry={selectedCountry}
                  />
                  {errors.phone?.message && (
                    <p className="text-red text-[13px] mt-0.5 rizzui-textarea-error-text">
                      {"" + errors.phone.message}
                    </p>
                  )}
                </div>
              )}
            />

            <Input
              type="email"
              label="Email Address"
              variant="text"
              rounded="md"
              inputClassName={inputClassName}
              labelClassName={labelClassName}
              {...register("email", {
                ...emailValidationPattern,
              })}
              error={errors.email?.message ? "" + errors.email.message : ""}
            />

            <Password
              label={<Text>Password</Text>}
              variant="text"
              rounded="md"
              inputClassName={inputClassName}
              labelClassName={labelClassName}
              {...register("password", {
                ...passwordValidationPattern,
              })}
              error={
                errors.password?.message ? "" + errors.password.message : ""
              }
            />

            <Button
              variant="solid"
              className="!bg-btn rounded-[4px] font-[500] text-[12px] leading-[15.84px] w-full"
              type="submit"
              isLoading={mutation?.status === "pending"}
            >
              Create Account
            </Button>
            <Text className="text-red-500 font-switzer text-[13px] mt-[-20px] mb-[8px]">
              {mutation?.error?.message}
            </Text>
          </form>
        </div>
      </div>
    </AccountBox>
  );
}
